import { supabase } from "@/integrations/supabase/client";

/**
 * Referential integrity utilities for admin operations
 */

export interface DependencyCheck {
  hasDependent: boolean;
  dependentCount: number;
  dependentType: string;
  message: string;
}

/**
 * Check if a brand has dependent products before deletion
 */
export const checkBrandDependencies = async (brandId: string): Promise<DependencyCheck> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id', { count: 'exact' })
      .eq('brand_id', brandId);

    if (error) {
      console.error('Error checking brand dependencies:', error);
      return {
        hasDependent: false,
        dependentCount: 0,
        dependentType: 'products',
        message: ''
      };
    }

    const count = data?.length || 0;

    return {
      hasDependent: count > 0,
      dependentCount: count,
      dependentType: 'produtos',
      message: count > 0
        ? `Esta marca tem ${count} produto${count > 1 ? 's' : ''} associado${count > 1 ? 's' : ''}. Elimine primeiro os produtos antes de eliminar a marca.`
        : ''
    };
  } catch (error) {
    console.error('Error in checkBrandDependencies:', error);
    return {
      hasDependent: false,
      dependentCount: 0,
      dependentType: 'products',
      message: ''
    };
  }
};

/**
 * Check if a category has dependent products or subcategories before deletion
 */
export const checkCategoryDependencies = async (categoryId: string): Promise<DependencyCheck> => {
  try {
    // Check for products using this category
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id', { count: 'exact' })
      .eq('category_id', categoryId);

    if (productsError) {
      console.error('Error checking category product dependencies:', productsError);
    }

    // Check for subcategories
    const { data: subcategories, error: subcategoriesError } = await supabase
      .from('categories')
      .select('id', { count: 'exact' })
      .eq('parent_id', categoryId);

    if (subcategoriesError) {
      console.error('Error checking category subcategory dependencies:', subcategoriesError);
    }

    const productCount = products?.length || 0;
    const subcategoryCount = subcategories?.length || 0;
    const totalDependents = productCount + subcategoryCount;

    if (totalDependents === 0) {
      return {
        hasDependent: false,
        dependentCount: 0,
        dependentType: '',
        message: ''
      };
    }

    let message = 'Esta categoria tem ';
    const parts = [];

    if (productCount > 0) {
      parts.push(`${productCount} produto${productCount > 1 ? 's' : ''}`);
    }

    if (subcategoryCount > 0) {
      parts.push(`${subcategoryCount} subcategoria${subcategoryCount > 1 ? 's' : ''}`);
    }

    message += parts.join(' e ') + ' associado' + (totalDependents > 1 ? 's' : '') + '. ';
    message += 'Elimine primeiro os dependentes antes de eliminar a categoria.';

    return {
      hasDependent: true,
      dependentCount: totalDependents,
      dependentType: 'produtos/categorias',
      message
    };
  } catch (error) {
    console.error('Error in checkCategoryDependencies:', error);
    return {
      hasDependent: false,
      dependentCount: 0,
      dependentType: '',
      message: ''
    };
  }
};

/**
 * Check if a store has dependent users before deletion
 */
export const checkStoreDependencies = async (storeId: string): Promise<DependencyCheck> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id', { count: 'exact' })
      .eq('main_store_id', storeId);

    if (error) {
      console.error('Error checking store dependencies:', error);
      return {
        hasDependent: false,
        dependentCount: 0,
        dependentType: 'users',
        message: ''
      };
    }

    const count = data?.length || 0;

    return {
      hasDependent: count > 0,
      dependentCount: count,
      dependentType: 'utilizadores',
      message: count > 0
        ? `Este local tem ${count} utilizador${count > 1 ? 'es' : ''} associado${count > 1 ? 's' : ''}. Transfira primeiro os utilizadores para outro local antes de eliminar este.`
        : ''
    };
  } catch (error) {
    console.error('Error in checkStoreDependencies:', error);
    return {
      hasDependent: false,
      dependentCount: 0,
      dependentType: 'users',
      message: ''
    };
  }
};

/**
 * Generic dependency checker that can be extended for other entities
 */
export const checkDependencies = async (
  entityType: 'brand' | 'category' | 'store',
  entityId: string
): Promise<DependencyCheck> => {
  switch (entityType) {
    case 'brand':
      return checkBrandDependencies(entityId);
    case 'category':
      return checkCategoryDependencies(entityId);
    case 'store':
      return checkStoreDependencies(entityId);
    default:
      return {
        hasDependent: false,
        dependentCount: 0,
        dependentType: '',
        message: ''
      };
  }
};